<script setup>
import { computed, watch } from 'vue'

const emit = defineEmits(['change', 'input'])

const props = defineProps({
  rows: {
    type: Number,
    required: true,
  },
  perPage: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: 'secondary',
    validators: value => ['primary', 'secondary'].includes(value),
  },
  stylePagination: {
    type: String,
    default: 'blue',
  },
})

const model = defineModel({ type: [Number, String] })

const getPerPage = computed(() => {
  return props.perPage >= 0 ? props.perPage : props.rows
})

const totalPages = computed(() => {
  const perPage = parseInt(props.perPage >= 0 ? props.perPage : -1 * props.perPage, 10)
  if (props.rows < perPage) {
    return 1
  }
  if (props.rows % perPage === 0) {
    return props.rows / perPage
  }
  return parseInt(props.rows / perPage, 10) + 1
})

const optionsArray = computed(() => {
  if (props.perPage < 0) {
    return 1
  }
  if (totalPages.value <= 3) {
    return totalPages.value
  }
  if (model.value === 1) {
    return 3
  }
  if (model.value > 1 && model.value < totalPages.value) {
    return [parseInt(model.value, 10) - 1, parseInt(model.value, 10), parseInt(model.value, 10) + 1]
  }
  if (model.value === totalPages.value) {
    return [parseInt(model.value, 10) - 2, parseInt(model.value, 10) - 1, parseInt(model.value, 10)]
  }
  return 1
})

const changePage = index => {
  if (model.value !== index) {
    model.value = index
    emit('input', index)
  }
}

const previousPage = () => {
  if (model.value > 1) changePage(model.value - 1)
}

const nextPage = () => {
  if (model.value < totalPages.value) changePage(model.value + 1)
}

watch(
  () => model.value,
  newValue => {
    if (typeof newValue === 'string') {
      model.value = parseInt(newValue, 10)
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="flex items-center justify-between py-3">
    <!-- <div class="flex flex-1 justify-between sm:hidden">
      <button
        @click="previousPage"
        type="button"
        class="text-white relative inline-flex items-center rounded-md border-none px-4 py-2 text-sm font-medium"
        :class="[
          {
            'bg-primary-10 hover:bg-primary-30': variant === 'primary',
            'bg-secondary-50 hover:bg-secondary-60': variant === 'secondary',
          },
        ]"
      >
        Anterior
      </button>
      <button
        @click="nextPage"
        type="button"
        class="text-white relative inline-flex items-center rounded-md border-none px-4 py-2 text-sm font-medium"
        :class="[
          {
            'bg-primary-10 hover:bg-primary-30': variant === 'primary',
            'bg-secondary-50 hover:bg-secondary-60': variant === 'secondary',
          },
        ]"
      >
        Próximo
      </button>
    </div> -->
    <div
      class="flex w-full flex-col-reverse items-center gap-4 sm:flex-1 sm:flex-row sm:justify-between"
    >
      <div>
        <p class="text-neutral-40 text-sm font-light">
          Exibindo
          <span class="font-light">
            {{ model === 1 ? 1 : getPerPage * (model - 1) + 1 }}
          </span>
          a
          <span class="font-light">
            {{ getPerPage * model + (model - 1) > rows ? rows : getPerPage * model }}
          </span>
          de
          <span class="font-light"> {{ rows }}</span>
          resultados
        </p>
      </div>
      <div>
        <nav class="relative z-0 inline-flex gap-2 -space-x-px rounded-md" aria-label="Pagination">
          <ImoButton
            @click="previousPage"
            type="button"
            variant="tertiary"
            icon="arrow-left-02"
            :disabled="model === 1"
          >
            <span class="sr-only">Anterior</span>
          </ImoButton>

          <button
            v-for="index in optionsArray"
            :key="index"
            @click="changePage(index)"
            aria-current="page"
            type="button"
            class="inline-flex items-center px-3 py-1 text-sm font-medium"
            :class="[
              {
                'bg-primary-90 hover:bg-primary-80 rounded-md transition hover:text-white':
                  variant === 'primary',
                'bg-secondary-50 hover:bg-secondary-60 rounded-md transition hover:text-white':
                  variant === 'secondary',
                'z-10 text-base font-bold text-white': index === model,
                'text-primary-80 bg-transparent text-base': index !== model,
              },
            ]"
          >
            {{ index }}
          </button>
          <ImoButton
            @click="nextPage"
            type="button"
            variant="tertiary"
            icon="arrow-right-02"
            :disabled="model === totalPages"
          >
            {{ totalPages }}
            <span class="sr-only">Próximo</span>
            <ImoIcon name="arrow-right-02" />
          </ImoButton>
        </nav>
      </div>
    </div>
  </div>
</template>
